
import React, { useState, useRef } from 'react';
import { Factory, Stethoscope, ShoppingBag, PieChart, ArrowUpRight, Globe, TrendingUp, CheckCircle2, ChevronLeft, ChevronRight, Lock, Quote } from 'lucide-react';

const industryFits = [
  {
    icon: <Factory className="w-6 h-6" />,
    name: 'Manufacturing',
    description: 'Production planning and supply chain coordination across complex operations.'
  },
  {
    icon: <Stethoscope className="w-6 h-6" />,
    name: 'Healthcare',
    description: 'Resource allocation and patient flow decisions under clinical and operational pressure.'
  },
  {
    icon: <ShoppingBag className="w-6 h-6" />,
    name: 'Retail',
    description: 'Inventory and demand decisions across volatile multichannel environments.'
  },
  {
    icon: <PieChart className="w-6 h-6" />,
    name: 'Private Equity',
    description: 'Portfolio level visibility where operational decisions impact EBITDA and risk.'
  }
];

const painPoints = [
  {
    quote: "The data exists in five different systems, but none of them talk to each other when a shipment is actually late.",
    role: "VP of Supply Chain",
    context: "Logistics"
  },
  {
    quote: "We spend more time in 'war rooms' reacting to failures than we do building the business.",
    role: "Chief Operating Officer",
    context: "Manufacturing"
  },
  {
    quote: "I have 40 dashboards, yet I'm still the last person to know when a production line stops.",
    role: "Director of Operations",
    context: "Industrial"
  },
  {
    quote: "Operational 'drift' is costing us millions, but it's invisible until the quarterly audit.",
    role: "Finance Director",
    context: "Enterprise Tech"
  },
  {
    quote: "Our teams are drowning in alerts, so they've started ignoring the ones that actually matter.",
    role: "Ops Manager",
    context: "Healthcare"
  },
  {
    quote: "Scaling is impossible when every new facility requires a manual coordination miracle to stay aligned.",
    role: "Head of Strategy",
    context: "Retail"
  },
  {
    quote: "We are implementing 'AI' but still rely on massive spreadsheets for our most critical execution decisions.",
    role: "Chief Transformation Officer",
    context: "Enterprise"
  },
  {
    quote: "By the time we identify a performance drop in a specific region, the customer has usually already churned.",
    role: "VP of Customer Success",
    context: "SaaS Services"
  }
];

const caseStudies = [
  {
    title: "Medical Equipment Distribution",
    location: "Japan",
    metric: "~¥98B annual turnover",
    context: "One of Japan’s largest medical equipment suppliers.",
    outcome: "Across a complex medical supply network, Groflex surfaced early inventory and demand imbalances, enabling teams to intervene before shortages, oversupply, or delivery delays reached hospitals.",
    type: "active"
  },
  {
    title: "Manufacturing",
    location: "Europe",
    metric: "Multi-line Production",
    context: "Multi-line production with interdependent throughput constraints.",
    outcome: "Groflex revealed hidden production bottlenecks. Leveraging integrated Scenario Planning, teams simulated execution shifts to act before delays cascaded into throughput loss and unplanned downtime.",
    type: "active"
  },
  {
    title: "Hospital",
    location: "India",
    metric: "High-Volume Operations",
    context: "High-volume patient operations across clinical and administrative flows.",
    outcome: "By processing massive volumes of Unstructured Data from clinical records and logs, Groflex exposed cycle-time delays and revenue leakage points previously hidden across departments.",
    type: "active"
  },
  {
    title: "Supply Chain",
    location: "Central Asia",
    metric: "Cross-Border Planning",
    context: "Cross-border planning in a volatile supply environment.",
    outcome: "Powered by our core Predictions Feature, Groflex surfaced early risk signals and fulfilment variance across planning cycles, enabling mitigation actions to be tested before disruptions materialised.",
    type: "active"
  },
  {
    title: "Retail",
    location: "Europe",
    metric: "Multi-Node Logistics",
    context: "Inventory and distribution points management.",
    outcome: "Optimizing dynamic stock allocation and distribution node performance across pan-European networks to proactively mitigate regional fulfillment lag and overstock scenarios.",
    type: "coming-soon"
  },
  {
    title: "Private Equity",
    location: "US",
    metric: "Portfolio Intelligence",
    context: "Real-time EBITDA risk monitoring across diverse portfolio companies.",
    outcome: "Aggregating operational health signals from disparate portfolio assets to identify systemic risks and value-creation opportunities automatically.",
    type: "coming-soon"
  }
];

export const WhoItsFor: React.FC = () => {
  const [hoveredCase, setHoveredCase] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const painScrollRef = useRef<HTMLDivElement>(null);

  const scroll = (ref: React.RefObject<HTMLDivElement>, direction: 'left' | 'right') => {
    if (ref.current) {
      const { scrollLeft } = ref.current;
      const firstChild = ref.current.firstElementChild as HTMLElement;
      
      if (firstChild) {
        const itemWidth = firstChild.offsetWidth;
        const gap = 24; // Matching the gap-6 (1.5rem / 24px) Tailwind class
        const step = itemWidth + gap;
        const scrollTo = direction === 'left' ? scrollLeft - step : scrollLeft + step;
        
        ref.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
      }
    }
  };

  return (
    <section id="solutions" className="py-32 px-6 md:px-12 bg-brandBg text-zodiac overflow-hidden border-t border-zodiac/5">
      <div className="max-w-7xl mx-auto">
        
        {/* OPERATIONAL FIT GRID */}
        <div className="max-w-3xl mb-24">
          <div className="inline-flex items-center space-x-3 mb-6">
            <span className="w-8 h-px bg-accent"></span>
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-accent">OPERATIONAL FIT</p>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 tracking-tighter leading-tight">
            Built for Organizations Operating in Complexity
          </h2>
          <p className="text-xl text-zodiac/60 leading-relaxed font-light">
            Groflex is designed for organizations where decisions are interconnected, time-critical, and costly to get wrong.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-32">
          {industryFits.map((fit, i) => (
            <div key={i} className="p-10 bg-white rounded-[2rem] border border-zodiac/5 shadow-sm hover:shadow-xl transition-executive group">
              <div className="w-12 h-12 rounded-2xl bg-brandBg flex items-center justify-center mb-8 text-zodiac group-hover:bg-accent group-hover:text-white transition-all duration-500">
                {fit.icon}
              </div>
              <h4 className="text-lg font-bold mb-4 tracking-tight uppercase group-hover:text-accent transition-colors">{fit.name}</h4>
              <p className="text-sm text-zodiac/50 font-medium leading-relaxed">
                {fit.description}
              </p>
            </div>
          ))}
        </div>

        {/* VOICE OF THE OPERATOR: PAIN POINTS SCROLL */}
        <div className="mb-48">
          <div className="flex justify-between items-end mb-12">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.4em] text-accent mb-4">VOICE OF THE OPERATOR</p>
              <h3 className="text-2xl font-bold text-zodiac tracking-tight">Does this sound familiar?</h3>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => scroll(painScrollRef, 'left')}
                className="w-10 h-10 rounded-full border border-zodiac/10 flex items-center justify-center hover:bg-accent hover:text-white transition-all text-zodiac"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button 
                onClick={() => scroll(painScrollRef, 'right')}
                className="w-10 h-10 rounded-full border border-zodiac/10 flex items-center justify-center hover:bg-accent hover:text-white transition-all text-zodiac"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          <div 
            ref={painScrollRef}
            className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar gap-6 pb-4 scroll-smooth"
          >
            {painPoints.map((pain, idx) => (
              <div 
                key={idx}
                className="flex-shrink-0 w-[85vw] md:w-[400px] snap-start bg-white p-10 rounded-[2.5rem] border border-zodiac/5 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group"
              >
                <Quote className="absolute -top-4 -right-4 w-24 h-24 text-zodiac/[0.03] group-hover:text-accent/[0.05] transition-colors" />
                <p className="text-lg md:text-xl font-medium italic text-zodiac/80 mb-10 leading-relaxed relative z-10">
                  "{pain.quote}"
                </p>
                <div className="flex items-center gap-4 relative z-10">
                  <div className="w-8 h-px bg-accent/30"></div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-zodiac">{pain.role}</p>
                    <p className="text-[9px] font-bold text-zodiac/30 uppercase tracking-widest mt-0.5">{pain.context}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* PROOF IN PRACTICE: HORIZONTAL SCROLLABLE LAYOUT */}
        <div className="relative">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center space-x-3 mb-6">
                <span className="w-8 h-px bg-accent"></span>
                <p className="text-[10px] font-black uppercase tracking-[0.4em] text-accent">PROOF IN PRACTICE</p>
              </div>
              <h3 className="text-3xl md:text-5xl font-bold text-zodiac tracking-tight">
                Global Operational Success
              </h3>
              <p className="text-lg md:text-xl text-zodiac/60 font-light mt-6 leading-relaxed">
                Real outcomes delivered across complex environments from healthcare and manufacturing to global supply chains.
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => scroll(scrollRef, 'left')}
                className="w-12 h-12 rounded-full border border-zodiac/10 flex items-center justify-center hover:bg-accent hover:text-white transition-all text-zodiac"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button 
                onClick={() => scroll(scrollRef, 'right')}
                className="w-12 h-12 rounded-full border border-zodiac/10 flex items-center justify-center hover:bg-accent hover:text-white transition-all text-zodiac"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div 
            ref={scrollRef}
            className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar gap-8 pb-12 cursor-grab active:cursor-grabbing scroll-smooth"
          >
            {caseStudies.map((study, idx) => (
              <div 
                key={idx}
                onMouseEnter={() => setHoveredCase(idx)}
                onMouseLeave={() => setHoveredCase(null)}
                className={`
                  relative flex-shrink-0 w-[90vw] md:w-[calc(50%-16px)] snap-start group bg-white rounded-[2.5rem] border transition-all duration-700 overflow-hidden flex flex-col
                  ${hoveredCase === idx ? 'border-accent/40 shadow-2xl scale-[0.99]' : 'border-zodiac/5 shadow-sm'}
                  ${study.type === 'coming-soon' ? 'opacity-70 bg-brandBg/50 grayscale' : ''}
                `}
              >
                {/* Visual Header */}
                <div className="p-8 lg:p-10 border-b border-zodiac/5 bg-zodiac/[0.01]">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-2 text-[10px] font-black uppercase tracking-[0.3em] text-accent">
                      <TrendingUp className="w-3.5 h-3.5" />
                      <span>{study.location}</span>
                    </div>
                    {study.type === 'coming-soon' ? (
                      <div className="px-3 py-1 bg-zodiac/10 border border-zodiac/20 rounded-full text-[9px] font-black text-zodiac uppercase tracking-widest flex items-center gap-1.5">
                        <Lock className="w-3 h-3" />
                        Coming Soon
                      </div>
                    ) : (
                      <div className="px-3 py-1 bg-accent/5 border border-accent/20 rounded-full text-[9px] font-black text-accent uppercase tracking-widest">
                        {study.metric}
                      </div>
                    )}
                  </div>
                  <h4 className="text-2xl font-bold text-zodiac tracking-tight mb-2 group-hover:text-accent transition-colors">
                    {study.title}
                  </h4>
                  <p className="text-sm text-zodiac/40 font-bold uppercase tracking-widest leading-relaxed">
                    {study.context}
                  </p>
                </div>

                {/* Outcome Content */}
                <div className="p-8 lg:p-10 flex flex-col justify-between flex-grow h-[340px]">
                  <div>
                    <div className="inline-flex items-center space-x-2 mb-6 p-2 px-4 rounded-xl bg-brandBg">
                      <CheckCircle2 className="w-4 h-4 text-accent" />
                      <span className="text-[10px] font-black uppercase tracking-[0.4em] text-zodiac">
                        {study.type === 'coming-soon' ? 'Target Outcome' : 'Active Pilot'}
                      </span>
                    </div>
                    <p className={`text-lg leading-relaxed mb-10 ${study.type === 'coming-soon' ? 'text-zodiac/40 italic' : 'text-zodiac/70 font-light'}`}>
                      {study.outcome}
                    </p>
                  </div>
                  
                  <div className="mt-auto pt-8 border-t border-zodiac/5 flex items-center justify-between">
                    {study.type === 'coming-soon' ? (
                      <button className="flex items-center space-x-2 text-[10px] font-black uppercase tracking-widest text-zodiac/30 cursor-not-allowed">
                        <span>Notify on Launch</span>
                        <ArrowUpRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <a 
                        href="mailto:hello@groflex.ai"
                        className="flex items-center space-x-2 text-[10px] font-black uppercase tracking-widest text-accent hover:opacity-70 transition-opacity"
                      >
                        <span>Request Case Study</span>
                        <ArrowUpRight className="w-4 h-4" />
                      </a>
                    )}
                    <div className="w-8 h-8 rounded-full bg-zodiac/5 flex items-center justify-center text-[10px] font-black text-zodiac/20">
                      0{idx + 1}
                    </div>
                  </div>
                </div>

                {/* Hover progress bar */}
                {hoveredCase === idx && study.type !== 'coming-soon' && (
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-accent/10">
                    <div className="h-full bg-accent w-full origin-left animate-[marquee_3s_linear_infinite]"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
